import { ref, computed, watch } from 'vue'
import { useStore } from 'vuex'
import { isEqual } from 'lodash'

import apiMenu from '@/api/menu'

import { toastFunc } from '@/plugins/toastPlugin'

/** @module KP_06-Template */

/**
 * функцию композиции
 * @param {object} param - параметры для композиции
 * @param {function} param.applyTemplate - callback для применепя фильтров в customisationMenu
 * @param {function} param.resetTemplate - callback для сброса фильтров в customisationMenu
 *
 * @returns {object} obj
 */
export default function ({
  goal,
  menuType,
  applyTemplate = () => {},
  resetTemplate = () => {},
  afterSendingMealsTemplate = () => {},
  testShowTemplatesWarningForm = () => {},
}) {
  const store = useStore()
  const isLoggedIn = computed(() => store.getters['client/isLoggedIn'])
  /**
   * Отвечает за показ модалки с предупреждением
   * о наличии не кастомных дней
   */
  const isShowTemplatesWarningForm = ref(false)
  /**
   * Изменить значение переменной isShowTemplatesWarningForm
   * и показать модалку о наличии не кастомных дней
   * @param {boolean} val - показать или скрыть модалку
   */
  const setShowTemplatesWarningForm = (val) => {
    isShowTemplatesWarningForm.value = val
  }
  /**
   * Отвечает за показ модалки с предупреждением
   * об отсутсвии возможности настроить меню по шаблонам питания.
   * Показываем раз за сессию, если переключился на некастомный тариф в неготовке, имея настройки КП
   */
  const isShowEmtyTemplatesWarning = ref(false)
  const setShowEmtyTemplatesWarning = (val) => {
    if (val) {
      if (goal.value === 'medium' && !window.sessionStorage.getItem('is_show_emty_templates_warning_opened_before')) {
        window.sessionStorage.setItem('is_show_emty_templates_warning_opened_before', val)
        isShowEmtyTemplatesWarning.value = val
      }
    } else {
      isShowEmtyTemplatesWarning.value = val
    }
  }

  const dontShowTemplatesWarningForm = ref(false)
  const setDontShowTemplatesWarningForm = (val) => {
    dontShowTemplatesWarningForm.value = val
  }

  const selectedMealsTemplate = ref(null)
  const selectedMealsTemplate2 = ref(null)

  const mealsTemplate = ref({
    hide: true,
  })

  const setMealsTemplate = (meals = {}) => {
    meals.hide = true
    if (meals?.templates && meals?.templates?.length > 0) {
      meals.hide = false
      meals.templates = [
        {
          templateId: null,
          frontName: 'Полное меню',
          frontDescription: '',
          mealNumbersToRemove: [],
        },
        ...meals.templates,
      ]
      if (selectedMealsTemplate.value) {
        const enabledTemplate = meals.templates.find(item => item.templateId === selectedMealsTemplate.value.templateId)
        selectedMealsTemplate.value = enabledTemplate
        selectedMealsTemplate2.value = enabledTemplate
      } else {
        const enabledTemplate = meals.templates.find(item => item.isEnabled)
        selectedMealsTemplate.value = enabledTemplate
        selectedMealsTemplate2.value = enabledTemplate
      }
    }
    if (selectedMealsTemplate.value?.templateId) {
      setShowEmtyTemplatesWarning(meals.hide)
    }
    mealsTemplate.value = meals
  }

  const sendingMealsTeplate = ref(false)
  const sendMealsTemplate = async ({ toast } = { toast: true }) => {
    sendingMealsTeplate.value = false
    try {
      // TODO filters/days
      const response = await apiMenu.postCustomizationInfoFilters({ menuType: menuType.value, templateId: selectedMealsTemplate2.value.templateId || undefined })
      const { success, messages, errorCode } = response.data
      if (success) {
        afterSendingMealsTemplate(selectedMealsTemplate2.value.templateId || undefined)
        if (toast) {
          toastFunc({ group: 'success', title: 'Настройки применены', text: 'Они будут применяться автоматически ко всем следующим заказам' })
        }
      } else {
        if (errorCode) {
          if (messages) {
            messages.forEach((message) => {
              toastFunc({ group: 'error', title: message })
            })
          }
        }
      }
    } catch (error) {
      const {
        data: {
          message = '',
          messages,
        } = {},
      } = error || {}
      if (message) {
        toastFunc({ group: 'error', title: `${message}` })
      }
      if (messages) {
        messages.forEach((message) => {
          toastFunc({ group: 'error', title: message })
        })
      }
    } finally {
      sendingMealsTeplate.value = false
    }
  }

  const confirmMealsTemplate = () => {
    selectedMealsTemplate2.value = selectedMealsTemplate.value
  }

  const cancelMealsTemplate = () => {
    selectedMealsTemplate.value = selectedMealsTemplate2.value
  }

  watch(selectedMealsTemplate, (val, oldValue) => {
    if (
      val?.templateId &&
      val?.templateId !== selectedMealsTemplate2.value.templateId &&
      !dontShowTemplatesWarningForm.value &&
      testShowTemplatesWarningForm()
    ) {
      isShowTemplatesWarningForm.value = true
    } else {
      confirmMealsTemplate()
    }
  })
  watch(selectedMealsTemplate2, (val, oldValue) => {
    if (val.templateId === oldValue.templateId) return
    if (isEqual(val.mealNumbersToRemove.sort(), oldValue.mealNumbersToRemove.sort())) return
    resetTemplate()
    if (isLoggedIn.value) {
      sendMealsTemplate({ toast: selectedMealsTemplate.value?.templateId != null })
    }
    setTimeout(() => {
      if (selectedMealsTemplate.value?.mealNumbersToRemove != null) {
        applyTemplate(selectedMealsTemplate.value?.mealNumbersToRemove)
      }
    }, 0)
  })

  return {
    // data
    mealsTemplate,
    selectedMealsTemplate,
    selectedMealsTemplate2,
    isShowTemplatesWarningForm,
    isShowEmtyTemplatesWarning,
    dontShowTemplatesWarningForm,
    sendingMealsTeplate,
    // computed
    // methods
    sendMealsTemplate,
    setShowTemplatesWarningForm,
    setDontShowTemplatesWarningForm,
    setShowEmtyTemplatesWarning,
    confirmMealsTemplate,
    cancelMealsTemplate,
    setMealsTemplate,
  }
}
